import React, { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { useForm } from "react-hook-form";
import {
  EMAIL_REGEX,
  createTrimHandler,
} from "../../utility/formValidationUtils";
import { validatePhoneNumber, formatToE164 } from "../../utility/phoneNumberUtils";
import ThemedInput from "./ThemedInput";
import ThemedTextArea from "./ThemedTextArea";
import PhoneNumberInput from "../Shared/PhoneNumberInput";
import { usePhoneNumber } from "../../hooks/usePhoneNumber";
import useScrollToFirstError from "../../hooks/useScrollToFirstError";
import useIsMobile from "../../hooks/useIsMobile";

const ContactConfirmationStep = forwardRef(({
  bookingPage,
  primaryColor,
  themeMode,
  contactFormData,
}, ref) => {
  const maxCharacters = 150;
  const [noteCharCount, setNoteCharCount] = useState(0);

  const {
    register,
    formState: { errors },
    setValue,
    watch,
    setError,
    clearErrors,
    trigger,
  } = useForm({
    defaultValues: {
      firstName: contactFormData.firstName || "",
      lastName: contactFormData.lastName || "",
      email: contactFormData.email || "",
      phoneNumber: contactFormData.phoneNumber || "",
      additionalNotes: contactFormData.additionalNotes || "",
    },
  });

  const { selectedCountry, onCountryChangeHandler, onInputChange, defaultCountry } = usePhoneNumber(
    contactFormData.phoneNumber
  );

  // Suppress the first-field autofocus on mobile so the on-screen keyboard
  // doesn't pop the moment this step mounts. Desktop keeps autofocus so the
  // cursor is ready to type. (React applies autoFocus only on mount.)
  const isMobile = useIsMobile();

  // Watch form values for character count
  const watchedAdditionalNotes = watch("additionalNotes");

  // Update character count for notes
  useEffect(() => {
    setNoteCharCount(watchedAdditionalNotes?.length || 0);
  }, [watchedAdditionalNotes]);

  // Smooth-scroll the first invalid field into view after failed validation.
  useScrollToFirstError(errors);

  // Expose validation trigger, form data getter, and server-error setter to parent.
  useImperativeHandle(ref, () => ({
    validateForm: async () => {
      const isValid = await trigger();
      return isValid;
    },
    getFormData: () => ({
      firstName: watch("firstName") || "",
      lastName: watch("lastName") || "",
      email: watch("email") || "",
      phoneNumber: formatToE164(watch("phoneNumber") || "", defaultCountry),
      additionalNotes: watch("additionalNotes") || "",
    }),
    // Called by the booking flow hook when the backend returns
    // VALIDATION_ERROR with details[]. Attaches each error inline on its field.
    setFieldErrors: (fieldErrors) => {
      Object.entries(fieldErrors || {}).forEach(([field, message]) => {
        setError(field, { type: "server", message });
      });
    },
    clearFieldErrors: () => clearErrors(),
  }), [trigger, watch, defaultCountry, setError, clearErrors]);

  // Custom phone number validation
  const validatePhoneNumberField = async (phoneNumber) => {
    if (!phoneNumber?.trim()) {
      return true; // Phone number is optional
    }

    try {
      const isValid = validatePhoneNumber(phoneNumber, setError, defaultCountry);
      if (!isValid) {
        return "Invalid phone number format";
      }
      return true;
    } catch (error) {
      return "Invalid phone number format";
    }
  };

  return (
    <div className="space-y-0">
      {/* Contact Information Form */}
      <div className="bg-themed-card border-themed rounded-lg shadow-lg border p-6">
        <h2 className="text-xl font-semibold text-themed-primary mb-4">
          Almost done — how can we reach you?
        </h2>

        <div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
          <div className="col-span-full sm:col-span-3">
            <ThemedInput
              id="firstName"
              name="firstName"
              label="First name"
              placeholder="e.g., Jane"
              autoFocus={!isMobile}
              errormessage={errors?.firstName?.message}
              primaryColor={primaryColor}
              themeMode={themeMode}
              {...register("firstName", {
                onBlur: createTrimHandler(setValue, "firstName", false),
                required: "First name is required",
                validate: (value) => {
                  if (!value?.trim()) {
                    return "First name is required";
                  }
                  return true;
                },
              })}
            />
          </div>

          <div className="col-span-full sm:col-span-3">
            <ThemedInput
              id="lastName"
              name="lastName"
              label="Last name"
              optional
              placeholder="e.g., Doe"
              errormessage={errors?.lastName?.message}
              primaryColor={primaryColor}
              themeMode={themeMode}
              {...register("lastName", {
                onBlur: createTrimHandler(setValue, "lastName", false),
              })}
            />
          </div>

          <div className="col-span-full">
            <ThemedInput
              id="email"
              name="email"
              type="email"
              label="Email"
              placeholder="name@example.com"
              errormessage={errors?.email?.message}
              primaryColor={primaryColor}
              themeMode={themeMode}
              {...register("email", {
                required: "Email is required",
                pattern: {
                  value: EMAIL_REGEX,
                  message: "Invalid email address",
                },
                validate: (value) => {
                  if (!value?.trim()) {
                    return "Email is required";
                  }
                  if (!EMAIL_REGEX.test(value.trim())) {
                    return "Invalid email address";
                  }
                  return true;
                },
              })}
            />
          </div>

          <div className="col-span-full">
            <PhoneNumberInput
              selectedCountry={selectedCountry}
              onCountryChangeHandler={onCountryChangeHandler}
              onInputChange={onInputChange}
              optional={true}
              errormessage={errors?.phoneNumber?.message}
              primaryColor={primaryColor}
              themeMode={themeMode}
              {...register("phoneNumber", {
                onBlur: createTrimHandler(setValue, "phoneNumber", false),
                validate: validatePhoneNumberField,
              })}
            />
          </div>

          <div className="col-span-full">
            <ThemedTextArea
              label="Additional notes"
              optional
              id="additionalNotes"
              name="additionalNotes"
              placeholder="Any special requests or additional information"
              errormessage={errors?.additionalNotes?.message}
              charCount={noteCharCount}
              maxLength={maxCharacters}
              primaryColor={primaryColor}
              themeMode={themeMode}
              {...register("additionalNotes", {
                maxLength: {
                  value: maxCharacters,
                  message: `Notes cannot exceed ${maxCharacters} characters`,
                },
                onBlur: (e) => {
                  setValue("additionalNotes", e.target.value.trim(), {
                    shouldValidate: false,
                  });
                },
                onChange: (e) => {
                  setNoteCharCount(e.target.value.trim().length);
                  setValue("additionalNotes", e.target.value, {
                    shouldValidate: false,
                  });
                },
              })}
            />
          </div>
        </div>
      </div>
    </div>
  );
});

export default ContactConfirmationStep;
